import type { TExample } from '@atlas/uicorn/preview/modules';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import useRefCallback from 'use-ref-callback';

function UnmountDelayComponent<T extends HTMLElement = HTMLElement>(
    props: TUnmountDelayProps<T>,
    ref: React.ForwardedRef<TUnmountDelayRef>,
): React.ReactElement {
    const { children, delay, onStartUnmount, onEndUnmount, onMountClone, onMountedClone, className = 'leave' } = props;

    const refs = useRef<{ element: Element; anchorComment: Comment; elementClone?: Element }[]>([]);

    const playedRef = useRef(false);

    const postponeChildrenUnmount = useRefCallback(() => {
        if (playedRef.current) return;
        playedRef.current = true;

        onStartUnmount?.();
        const unmounted = Array.from(refs.current);

        unmounted.forEach((ref) => {
            ref.elementClone = Object.assign(document.createElement('i'), {
                innerHTML: ref.element.outerHTML,
            }).children[0];
            if ('style' in ref.elementClone) {
                (ref.elementClone as HTMLElement).style.animation = 'none';
                (ref.elementClone as HTMLElement).style.transition = 'none';
            }

            onMountClone?.(ref.elementClone as T);
            ref.anchorComment.parentElement?.insertBefore(ref.elementClone, ref.anchorComment);
            onMountedClone?.(ref.elementClone as T);
            ref.elementClone.classList.add(className);
            ref.anchorComment.remove();
        });

        setTimeout(() => {
            unmounted.forEach((ref) => ref.elementClone?.remove());
            onEndUnmount?.();
        }, delay * 1e3);
    });

    useEffect(() => postponeChildrenUnmount, []);

    refs.current.splice(0).forEach((ref) => ref.anchorComment.remove());

    useImperativeHandle(ref, () => ({
        reset: () => {
            playedRef.current = false;
        },
        playOnce: () => {
            postponeChildrenUnmount();
            playedRef.current = true;
        },
    }));

    return (
        <>
            {React.Children.map(children, (child, index) => {
                const modifiedChild = child && {
                    ...React.cloneElement(child),
                    ref: (element: Element | null) => {
                        if (element !== null && !(element instanceof Element)) {
                            element = null;
                            console.error('UnmountDelay: Children ref must be an Element');
                        }

                        if (element?.parentElement) {
                            const anchorComment = document.createComment(`unmount-delay-anchor-${index}`);
                            element.parentElement.insertBefore(anchorComment, element);
                            refs.current.push({ element, anchorComment });
                        }

                        if (child && 'ref' in child) {
                            if (typeof child.ref === 'function') {
                                child.ref(element);
                            } else if (typeof child.ref === 'object' && child.ref !== null && 'current' in child.ref) {
                                child.ref.current = element;
                            }
                        }
                    },
                };
                return modifiedChild;
            })}
        </>
    );
}

const UnmountDelay = React.memo(React.forwardRef(UnmountDelayComponent)) as <T extends HTMLElement = HTMLElement>(
    props: TUnmountDelayProps<T> & { ref?: React.Ref<TUnmountDelayRef> },
) => React.ReactElement<TUnmountDelayProps<T>>;

export default UnmountDelay;

// #region Types

export type TUnmountDelayProps<T extends HTMLElement = HTMLElement> = {
    children?:
        | React.ReactElement
        | null
        | undefined
        | ''
        | false
        | Array<React.ReactElement | null | undefined | '' | false>;
    delay: number;
    className?: string;
    onStartUnmount?: () => void;
    onEndUnmount?: () => void;
    onMountClone?: (el: T) => void;
    onMountedClone?: (el: T) => void;
};

export type TUnmountDelayRef = { playOnce: () => void };

// #endregion

// #region Examples

const testEnterKeyframes = keyframes`
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
`;

const testLeaveKeyframes = keyframes`
    from {
        opacity: 1;
    }

    to {
        opacity: 0;
    }
`;

const STestAnimated = styled.p<{ $delay: number }>`
    animation: ${testEnterKeyframes} ${(p) => p.$delay}s ease forwards;

    &.leave {
        animation: ${testLeaveKeyframes} ${(p) => p.$delay}s ease forwards;
    }
`;

export const Example: TExample = {
    render(props) {
        const [visible, setVisible] = useState(true);
        const [visibleSpecific, setVisibleSpecific] = useState(true);

        const delay = props.delay || 1;

        return (
            <>
                <button children="Toggle" onClick={() => (setVisible((f) => !f), setVisibleSpecific(!0))} />
                <div style={{ height: 100, width: 200 }}>
                    {visible && (
                        <UnmountDelay
                            delay={delay}
                            onStartUnmount={props.onStartUnmount}
                            onEndUnmount={props.onEndUnmount}
                        >
                            <STestAnimated
                                $delay={delay}
                                className="classNamed"
                                ref={(e) => e && (e.innerText = `Referenced with className "${e.className}"`)}
                            >
                                Hello
                            </STestAnimated>
                            {visibleSpecific && (
                                <STestAnimated $delay={delay}>
                                    What's going?{' '}
                                    <button children="hide" onClick={() => setVisibleSpecific((f) => !f)} />
                                </STestAnimated>
                            )}
                        </UnmountDelay>
                    )}
                </div>
            </>
        );
    },
    controls: {
        delay: { type: 'number', initial: 1, step: 0.1 },
    },
    handlers: {
        onStartUnmount: 'console',
        onEndUnmount: 'console',
    },
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('UnmountDelay', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
